import React, { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../lib/firebase';

/** Map Firebase auth error codes to friendly, non-technical messages. */
function friendlyAuthError(code: string | undefined): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'Incorrect email or password. Please try again.';
    case 'auth/user-disabled':
      return 'This account has been deactivated. Please contact your school admin.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a few minutes before trying again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    default:
      return 'Sign-in failed. Please try again or contact support.';
  }
}

import { useAuth } from '../hooks/useAuth';
import Logo, { LogoIcon } from '../components/Logo';

type View = 'signIn' | 'forgotPassword';

export default function LoginPage() {
  const { signIn } = useAuth();
  const [view, setView] = useState<View>('signIn');

  // Sign-in state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Forgot password state
  const [resetEmail, setResetEmail] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (err) {
      const code = (err as { code?: string }).code;
      setError(friendlyAuthError(code));
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setResetBusy(true);
    setResetError(null);
    try {
      const fns = getFunctions(app, 'europe-west2');
      const resetFn = httpsCallable(fns, 'requestPasswordReset');
      await resetFn({ email: resetEmail });
      setResetSent(true);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'functions/invalid-argument') {
        setResetError('Please enter a valid email address.');
      } else {
        setResetError('Something went wrong. Please try again.');
      }
    } finally {
      setResetBusy(false);
    }
  };

  const switchToForgot = () => {
    // Pre-fill reset email from whatever the user typed in the sign-in form
    setResetEmail(email);
    setResetError(null);
    setResetSent(false);
    setView('forgotPassword');
  };

  const switchToSignIn = () => {
    setError(null);
    setView('signIn');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand-900 flex-col justify-between p-12">
        <div>
          <Logo variant="bars" size={40} theme="light" showWordmark />
        </div>
        <div>
          <blockquote className="text-brand-100 text-xl font-light leading-relaxed">
            "Supporting staff development and school improvement through clear, structured performance management."
          </blockquote>
        </div>
        <p className="text-brand-400 text-sm">© {new Date().getFullYear()} J Parker Software · PM Review</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-16 bg-white">
        <div className="mx-auto w-full max-w-sm">
          {/* Logo / wordmark (mobile) */}
          <div className="mb-10 lg:hidden">
            <Logo variant="bars" size={36} theme="dark" showWordmark />
          </div>

          {view === 'signIn' ? (
            <>
              <Link
                to="/"
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-8"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back to home
              </Link>
              <h2 className="text-3xl font-bold text-gray-900 mb-1">Welcome back</h2>
              <p className="text-sm text-gray-500 mb-8">Sign in to your school account</p>

              <form onSubmit={(e) => void handleSignIn(e)} className="space-y-5">
                <div>
                  <label htmlFor="email" className="label">Email address</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@school.ac.uk"
                    className="input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="password" className="label mb-0">Password</label>
                    <button
                      type="button"
                      onClick={switchToForgot}
                      className="text-xs text-brand-600 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="input"
                  />
                </div>

                {error && (
                  <div role="alert" className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="btn-primary w-full py-2.5"
                >
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={switchToSignIn}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-8"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back to sign in
              </button>

              <h2 className="text-3xl font-bold text-gray-900 mb-1">Reset password</h2>
              <p className="text-sm text-gray-500 mb-8">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {resetSent ? (
                <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-4 text-sm text-green-700 space-y-1">
                  <p className="font-medium">Check your inbox</p>
                  <p>If an account exists for <strong>{resetEmail}</strong>, a password reset link has been sent. Check your spam folder if it doesn't arrive within a few minutes.</p>
                  <button
                    type="button"
                    onClick={switchToSignIn}
                    className="mt-3 text-brand-600 hover:underline text-sm font-medium"
                  >
                    Return to sign in →
                  </button>
                </div>
              ) : (
                <form onSubmit={(e) => void handleForgotPassword(e)} className="space-y-5">
                  <div>
                    <label htmlFor="reset-email" className="label">Email address</label>
                    <input
                      id="reset-email"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      autoComplete="email"
                      placeholder="you@school.ac.uk"
                      className="input"
                      autoFocus
                    />
                  </div>

                  {resetError && (
                    <div role="alert" className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                      {resetError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={resetBusy}
                    className="btn-primary w-full py-2.5"
                  >
                    {resetBusy ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        {/* Footer links */}
        <div className="mt-10 text-center text-xs text-gray-400 space-x-4">
          <Link to="/privacy" className="hover:text-gray-600 hover:underline">Privacy Policy</Link>
          <span>·</span>
          <Link to="/cookies" className="hover:text-gray-600 hover:underline">Cookie Policy</Link>
          <span>·</span>
          <span>© {new Date().getFullYear()} PM Review</span>
        </div>
      </div>
    </div>
  );
}
